import {GQL_SESSION_HEARTBEAT_QUERY, SessionHeartbeatResponse} from "@/lib/queries/auth";
import {useLazyQuery} from "@apollo/client";
import {useCallback} from "react";
import {useIdleTimer} from "react-idle-timer";

interface UseSessionHeartbeatOptions {
  /** Timeout in milliseconds before considering user idle (default: 5 minutes) */
  timeout?: number;
  /** Interval in milliseconds for periodic heartbeat (default: 4 minutes) */
  heartbeatInterval?: number;
  /** API endpoint for heartbeat requests */
  apiEndpoint?: string;
  /** Enable debug logging */
  debug?: boolean;
  /** Enable the heartbeat functionality */
  enabled?: boolean;
}

interface UseSessionHeartbeatReturn {
  /** Manually send a heartbeat */
  sendHeartbeat: () => Promise<void>;
  /** Get the remaining time until idle */
  getRemainingTime: () => number;
  /** Reset the idle timer */
  reset: () => void;
  /** Activate the timer (resume from pause) */
  activate: () => void;
  /** Pause the timer */
  pause: () => void;
}

export const useSessionHeartbeat = (options: UseSessionHeartbeatOptions = {}): UseSessionHeartbeatReturn => {
  const {
    heartbeatInterval = 5 * 60 * 1000, // 5 minutes
  } = options;

  const [triggerSessionHeartbeat] = useLazyQuery<SessionHeartbeatResponse>(GQL_SESSION_HEARTBEAT_QUERY, {
    context: {
      noToast: true,
    },
  });

  const sendHeartbeat = useCallback(async (): Promise<void> => {
    await triggerSessionHeartbeat();
  }, [triggerSessionHeartbeat]);

  const onAction = useCallback((): void => {
    sendHeartbeat();
  }, [sendHeartbeat]);

  const {getRemainingTime, reset, activate, pause} = useIdleTimer({
    onAction,
    throttle: heartbeatInterval,
    crossTab: true,
    syncTimers: 2000 * 60,
    leaderElection: true,
    disabled: heartbeatInterval === 0,
  });

  return {
    sendHeartbeat,
    getRemainingTime,
    reset,
    activate,
    pause,
  };
};
